import { useState, useEffect, useRef, useCallback } from 'react';
import { AgentMessage } from '../types';

interface UseWebSocketProps {
  url: string;
  fileName: string;
  onMessage?: (message: AgentMessage) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
}

export const useWebSocket = ({ url, fileName, onMessage, onError, onClose }: UseWebSocketProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const ws = useRef<WebSocket | null>(null);

  const connect = useCallback(() => {
    try {
      // Add sessionId as a query parameter since WebSocket headers aren't supported in browsers
      // const wsUrl = `${url}?SessionId=${encodeURIComponent(sessionId)}`;
      const wsUrl = `${url}?file_name=${encodeURIComponent(fileName)}`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
      };

      ws.current.onmessage = (event) => {
        try {
          console.log('WebSocket message received:', event.data);
          if (event.data === 'EXIT') {
            console.log('Received EXIT signal, closing connection');
            ws.current?.close();
            return;
          }
          
          const message: AgentMessage = JSON.parse(event.data);
          console.log('WebSocket parsed message:', message.role, '|', message.content.substring(0, 50));
          setMessages(prev => [...prev, message]);
          onMessage?.(message);
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        onError?.(error);
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        onClose?.();
      };
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      onError?.(error);
    }
  }, [url, fileName, onMessage, onError, onClose]);

  const sendMessage = useCallback((message: any) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('Sending WebSocket message:', message);
      ws.current.send(JSON.stringify(message));
    } else {
      console.error('WebSocket not open, ready state:', ws.current?.readyState);
    }
  }, []);

  const disconnect = useCallback(() => {
    ws.current?.close();
  }, []);

  useEffect(() => {
    return () => {
      ws.current?.close();
    };
  }, []);

  const restoreMessages = useCallback((restoredMessages: AgentMessage[]) => {
    setMessages(restoredMessages);
  }, []);

  return {
    isConnected,
    messages,
    connect,
    sendMessage,
    disconnect,
    clearMessages: () => setMessages([]),
    restoreMessages
  };
};